import type { ReactElement, SyntheticEvent } from "react"
import { useContext } from "react"
import { Link } from "gatsby"
import Media from "@gatsby-plugin-generic-page/components/Media"
import type { GhostButtonIcon } from "@gatsby-plugin-generic-page/fragments/ghostButton"
import { getGhostButtonFlatData } from "@gatsby-plugin-generic-page/helpers/getCtaElementFlatData"
import { replaceLinkValues } from "@gatsby-plugin-generic-page/helpers/linkParser"
import { SquidexComponentType } from "@gatsby-plugin-generic-page/types/componentType"
import { ButtonGhost } from "@ionos-web-design-system/react"
import { typeMatches } from "@onestore-graphql"
import DynamicUdsIcon, {
  ICON_GROUP_SQUIDEX_TYPE,
} from "~/components/DynamicUdsIcon"
import UdsThemeProvider from "~/components/UdsThemeProvider"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import SearchUrlParametersContext from "~/context/SearchUrlParametersContext"
import type { BrandUdsIconsComponent } from "~/fragments/brandUdsIcons"
import type { ImageElementComponent } from "~/fragments/imageElement"
import type { SystemUdsIconsComponent } from "~/fragments/systemUdsIcons"
import { cn } from "~/helpers/cn"
import useAnchorProperties from "~/hooks/useAnchorProperties"
import { sendGaEvent } from "~/lib/analytics"
import isEmpty from "~/lib/isEmpty"
import type { CtaElement } from "../../fragments/ctaElement"

/**
 * Resolves the thing shown above the label. UDS icons are looked up by name in
 * their own set — a system name will not resolve in the brand set or the other
 * way round, and an unknown name renders nothing at all — so the set comes from
 * which component the editor picked, not from a guess.
 */
function getGhostButtonIcon(
  icon: GhostButtonIcon | undefined,
  isCompact: boolean
): ReactElement | null {
  if (isEmpty(icon)) {
    return null
  }

  // A fixed square around whatever the editor picked. UDS sizes the two icon
  // sets on different scales — `large` is 24px for a system icon and 64px for a
  // brand one — and an uploaded image has no inherent size at all, so a row
  // mixing them lands the labels at different heights. Pinning the slot keeps
  // them level whatever goes in it.
  // Arbitrary rem, not `h-8`/`h-12`: UDS remaps `--spacing-0…11` onto a
  // non-linear scale, so in that range a bare Tailwind number is not `4px * n`
  // — `h-8` resolves to 64px, which made the compact box larger than the
  // default one rather than smaller.
  const box = cn(
    "flex shrink-0 items-center justify-center",
    isCompact
      ? "h-[2rem] w-[2rem]"
      : "h-[3rem] w-[3rem] max-md:h-[2rem] max-md:w-[2rem]",
    "[&_img]:max-h-full [&_img]:max-w-full [&_img]:object-contain"
  )

  if (typeMatches(icon.__typename, SquidexComponentType.IMAGE_ELEMENT)) {
    const { image } = icon as ImageElementComponent

    return !isEmpty(image) ? (
      <span className={box}>
        <Media media={image} />
      </span>
    ) : null
  }

  const iconGroup = ICON_GROUP_SQUIDEX_TYPE[icon.__typename]

  if (isEmpty(iconGroup)) {
    return null
  }

  return (
    <span className={box}>
      <DynamicUdsIcon
        iconName={
          (icon as SystemUdsIconsComponent | BrandUdsIconsComponent).iconId
        }
        iconGroup={iconGroup}
        // UDS sizes the icon with an inline `width`/`height`, and spreads any
        // `style` of ours over it — so this is what lets the slot's own size
        // decide how big the icon draws, and lets that size be responsive.
        style={{ width: "100%", height: "100%" }}
      />
    </span>
  )
}

export type GhostButtonProps = {
  button: CtaElement
  className?: string
}

/**
 * Renders a Squidex `ghost-button` CTA as the UDS `ButtonGhost` tile — an icon
 * stacked above a label, used as a low-emphasis launcher. Only navigational
 * action types are modelled in the schema (external link, internal link and
 * scroll-to-section), so there is no modal/basket branch here.
 *
 * The colour scheme is pinned to light. The tile is a white card with dark text
 * by design, and it is typically placed on dark artwork (the full-video slider),
 * where the inherited on-colour scheme would otherwise resolve
 * `bg-surface-input-default` to near-black and leave the label looking like bare
 * text on the video.
 */
export default function GhostButton({
  button,
  className,
}: GhostButtonProps): ReactElement<GhostButtonProps> | null {
  const { currentPriceType } = usePriceTypeContext()
  const currentSearchUrlParameters = useContext(SearchUrlParametersContext)
  const {
    actionTypeName,
    text,
    title,
    icon,
    isCompact,
    gaEvent,
    anchorId,
    href,
    openInNewTab,
    pages,
  } = getGhostButtonFlatData(button, currentPriceType)
  const { href: anchorHref, onClick: anchorOnClick } =
    useAnchorProperties(anchorId)

  if (isEmpty(actionTypeName)) {
    return null
  }

  // Density is per button: a tile marked compact renders compact whatever its
  // neighbours do.
  const isCompactTile = isCompact ?? false

  const commonProps = {
    icon: getGhostButtonIcon(icon, isCompactTile),
    text,
    title,
    // Explicitly boolean: passing `undefined` through leaves UDS's `compact`
    // variant unresolved, and the tile renders with no padding, gap or
    // min-width — a bare label instead of a card.
    compact: isCompactTile,
    // Fills the space its row gives it, so a set of launcher tiles comes out
    // evenly sized rather than each shrinking to its own label.
    //
    // UDS clamps the label to one line to keep equal-width tiles aligned; the
    // design wraps instead, and a row here is already held to a shared height,
    // so a longer label can take two lines rather than being cut to an ellipsis.
    //
    // A phone gets the compact density whatever the editor chose: rows wrap to
    // two columns there, mixing densities leaves them visibly ragged, and the
    // full-size padding is more than a narrow screen can spare. These are UDS's
    // own compact values, as classes rather than the `compact` prop, so the
    // switch is CSS and needs no breakpoint hook.
    className: cn(
      "w-full [&_p]:line-clamp-none",
      !isCompactTile &&
        "max-md:min-w-[6rem] max-md:gap-[0.25rem] max-md:p-[1rem]",
      className
    ),
  }

  const handleGaClick = (): void => {
    sendGaEvent(gaEvent)
  }

  // Each action type differs only in the element `ButtonGhost` wraps, so the
  // tile itself is built once around whichever one applies.
  let linkElement: ReactElement | null = null

  if (
    typeMatches(actionTypeName, SquidexComponentType.SCROLL_TO_SECTION) &&
    !isEmpty(anchorId)
  ) {
    const handleAnchorClick = (event: SyntheticEvent): void => {
      handleGaClick()
      anchorOnClick(event)
    }

    linkElement = (
      <Link title={title} to={anchorHref} onClick={handleAnchorClick} />
    )
  } else if (
    typeMatches(actionTypeName, SquidexComponentType.INTERNAL_LINK) &&
    !isEmpty(pages)
  ) {
    linkElement = (
      <Link title={title} to={pages[0].flatData.url} onClick={handleGaClick} />
    )
  } else if (
    typeMatches(actionTypeName, SquidexComponentType.EXTERNAL_LINK) &&
    !isEmpty(href)
  ) {
    linkElement = (
      // biome-ignore lint/a11y/useAnchorContent: asChild — ButtonGhost injects the icon and label
      <a
        href={replaceLinkValues(href, currentSearchUrlParameters)}
        title={title}
        target={openInNewTab ? "_blank" : undefined}
        rel={openInNewTab ? "noopener noreferrer" : undefined}
        onClick={handleGaClick}
      />
    )
  }

  if (linkElement === null) {
    return null
  }

  return (
    <UdsThemeProvider forceColorScheme="light">
      <ButtonGhost asChild {...commonProps}>
        {linkElement}
      </ButtonGhost>
    </UdsThemeProvider>
  )
}
